import React, { useState } from 'react';
import braintree from 'braintree-web';
import {
  Box,
  Button,
  Typography,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Formik, Form } from 'formik';
import AccountForm from 'renderer/features/settings/components/AccountForm';
import BillingAddressForm from 'renderer/features/settings/components/BillingAddressForm';
import { useAppSelector } from 'renderer/shared/state/hooks';
import {
  selectIsLoggedIn,
  selectUser,
} from 'renderer/features/auth/state/userSelectors';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import {
  useGetConfigQuery,
  useMakePurchaseMutation,
} from 'renderer/features/purchase-page/services/purchase.endpoints';
import { useGetCartQuery } from 'renderer/features/payments-page/services/cart.endpoints';
import PaymentMethodForm from 'renderer/features/purchase-page/components/PaymentMethodForm';
import ExistingPaymentMethods from '../../purchase-page/components/ExistingPaymentMethods';
import StripeCreditCardForm from '../../purchase-page/components/StripeCreditCardForm';

const steps = ['Personal Info', 'Billing Address', 'Payment Method', 'Review'];

const CheckoutPage = () => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const stripe = useStripe();
  const elements = useElements();
  const [makePurchase] = useMakePurchaseMutation();
  const { data: config, error: configError } = useGetConfigQuery();
  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);
  const { data: cartData } = useGetCartQuery(null);
  const isUsingStripe = cartData?.items.some(
    (item: any) => item.productId?.category === 'Subscription',
  );
  console.log('isUsingStripe', isUsingStripe);
  // console.log('cartData', cartData);
  const braintreeClientToken = config?.braintreeClientToken;
  // const handleSubmit = async (values: any) => {
  //   setIsSubmitting(true);
  //   setTimeout(() => {
  //     setIsSubmitting(false);
  //     window.location.href = '/#/confirmation';
  //   }, 2000);
  // };

  // const handleSubmit = async (values: any) => {
  //   setIsSubmitting(true);

  //   let newPaymentMethodToken = '';
  //   const provider = 'stripe'; // Assume Stripe for now

  //   if (!stripe || !elements) {
  //     alert('Stripe is not ready');
  //     setIsSubmitting(false);
  //     return;
  //   }

  //   const cardElement = elements.getElement(CardNumberElement);
  //   console.log('Card Element', cardElement);
  //   if (!cardElement) {
  //     alert('Card info is missing');
  //     setIsSubmitting(false);
  //     return;
  //   }

  //   const payload = await stripe.createPaymentMethod({
  //     type: 'card',
  //     card: cardElement,
  //   });
  //   console.log('Stripe payload');
  //   console.log(payload.paymentMethod)
  //   if (payload.error || !payload.paymentMethod) {
  //     alert(payload.error?.message || 'Failed to create payment method');
  //     setIsSubmitting(false);
  //     return;
  //   }

  //   newPaymentMethodToken = payload.paymentMethod.id;

  //   const {
  //     firstName,
  //     lastName,
  //     email,
  //     streetAddress,
  //     locality,
  //     region,
  //     postalCode,
  //     countryName,
  //   } = values;

  //   const purchaseRequest = {
  //     user: {
  //       firstName,
  //       lastName,
  //       email,
  //       create: !isLoggedIn,
  //       password: '', // handle if needed
  //       confirmPassword: '',
  //     },
  //     paymentMethod: {
  //       provider,
  //       token: newPaymentMethodToken,
  //       create: true,
  //       makeDefault: true,
  //       billingAddress: {
  //         nameOnCard: `${firstName} ${lastName}`,
  //         streetAddress,
  //         locality,
  //         region,
  //         postalCode,
  //         countryName,
  //       },
  //     },
  //     cartId: cartData?._id,
  //     purchases: cartData?.items.map((item: any, index: number) => ({
  //       productId: item.productId._id || item.productId,
  //       numberOfCopies: item.quantity,
  //       platform: item.platform,
  //       hostApplication: item.hostApp,
  //       serial: item.serial,
  //       isFinal: index === cartData.items.length - 1,
  //     })),
  //   };

  //   try {
  //     await makePurchase(purchaseRequest).unwrap();
  //     window.location.href = '/#/confirmation';
  //   } catch (err) {
  //     alert('Purchase failed. Please try again.');
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  const handleSubmit = async (values: any) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    let newPaymentMethodToken = '';
    const provider = 'stripe';

    // Create Stripe payment method if necessary
    if (values.isNewPaymentMethod) {
      if (!stripe || !elements) {
        alert('Stripe is not ready');
        setIsSubmitting(false);
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        alert('Card info is missing');
        setIsSubmitting(false);
        return;
      }

      const payload = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (payload.error || !payload.paymentMethod) {
        alert(payload.error?.message || 'Failed to create payment method');
        setIsSubmitting(false);
        return;
      }

      newPaymentMethodToken = payload.paymentMethod.id;
    }

    const {
      createAccount,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      savePayment,
      makeDefault,
      existingPaymentMethodToken,
      isNewPaymentMethod,
      nameOnCard,
      streetAddress,
      locality,
      region,
      postalCode,
      countryName,
    } = values;

    const request = {
      user: {
        id: isLoggedIn ? undefined : undefined, // replace with `user._id` if available
        create: createAccount,
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
      },
      paymentMethod: {
        provider,
        id: isNewPaymentMethod ? undefined : existingPaymentMethodToken,
        create: isNewPaymentMethod ? savePayment : undefined,
        makeDefault: isNewPaymentMethod ? makeDefault : undefined,
        token: isNewPaymentMethod ? newPaymentMethodToken : undefined,
        billingAddress: isNewPaymentMethod
          ? {
              nameOnCard,
              streetAddress,
              locality,
              region,
              postalCode,
              countryName,
            }
          : undefined,
      },
      cartId: cartData?._id,
      purchases: cartData?.items.map((item: any, index: number) => ({
        productId: item.productId._id || item.productId,
        numberOfCopies: item.quantity,
        platform: item.platform,
        hostApplication: item.hostApplication,
        serial: item.serial,
        isFinal: index === cartData.items.length - 1,
      })),
    };

    console.log('✅ Submitting request:', request);

    try {
      await makePurchase(request).unwrap();
      window.location.href = '/#/confirmation';
    } catch (err) {
      console.error('❌ Purchase failed', err);
      alert('Purchase failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '1000px', mx: 'auto', p: 4 }}>
      <Typography variant="h4" mb={3}>
        Checkout
      </Typography>

      {/* <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper> */}
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          '& .MuiStepLabel-root .Mui-completed': {
            color: 'success.main', // color for completed steps
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: 'primary.main', // color for active step label
          },
          '& .MuiStepLabel-label': {
            color: 'text.secondary', // default (inactive) step label color
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Formik
        initialValues={{
          nameOnCard: '',
          email: '',
          firstName: '',
          lastName: '',
          streetAddress: '',
          locality: '',
          region: '',
          regionCode: '',
          postalCode: '',
          countryName: '',
          countryCode: '',
          password: '',
          confirmPassword: '',
          createAccount: false,
          savePayment: false,
          makeDefault: false,
          discountCode: '',
          hostApplication: {
            id: '',
            version: '',
            displayName: '',
          },
          platform: '',
          isNewPaymentMethod: true,
          newPaymentMethodToken: '',
          numberValidity: '',
          cvvValidity: '',
          expirationDateValidity: '',
          existingPaymentMethodToken: '',
          numberOfCopies: 1,
          selectedPlan: '',
          serial: '',
        }}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ values, setFieldValue, isValid, dirty, errors, touched }) => {
          const isStepValid = () => {
            console.log('isStepValid', { isValid, dirty, errors, touched });
            if (activeStep === 0) {
              return (
                values.firstName &&
                values.lastName &&
                values.email &&
                (!values.createAccount ||
                  (values.password && values.confirmPassword)) &&
                Object.keys(errors).length === 0
              );
            }

            if (activeStep === 1) {
              return (
                values.streetAddress &&
                values.locality &&
                values.region &&
                values.postalCode &&
                values.countryName &&
                Object.keys(errors).length === 0
              );
            }

            // if (activeStep === 2) {
            //   return (
            //     values.isNewPaymentMethod || !!values.existingPaymentMethodToken
            //   );
            // }

            // if (activeStep === 2) {
            //   // New card = Stripe, or existing token = previously saved card
            //   return (
            //     (values.isNewPaymentMethod && values.newPaymentMethodToken) ||
            //     (!values.isNewPaymentMethod &&
            //       values.existingPaymentMethodToken)
            //   );
            // }

            if (activeStep === 2) {
              const requiredFields = [
                'numberValidity',
                'cvvValidity',
                'expirationDateValidity',
              ];

              const allTouched = requiredFields.every(
                (field: any) => (touched as any)[field],
              );
              const noErrors = requiredFields.every(
                (field) => !(errors as any)[field],
              );

              return allTouched && noErrors;
            }

            return true; // Review step
          };

          return (
            <Form>
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      {activeStep === 0 && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Personal Information
                          </Typography>
                          <AccountForm
                            disabled={false}
                            // setFieldValue={setFieldValue}
                            // values={values}
                          />
                        </>
                      )}

                      {activeStep === 1 && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Billing Address
                          </Typography>
                          <BillingAddressForm
                            disabled={false}
                            // setFieldValue={setFieldValue}
                            // values={values}
                          />
                        </>
                      )}

                      {activeStep === 2 && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Payment Method
                          </Typography>
                          {/* {values.isNewPaymentMethod ? (
                            <StripeCreditCardForm />
                          ) : (
                            <ExistingPaymentMethods />
                          )} */}
                        </>
                      )}

                      <Box
                        style={{ display: activeStep === 2 ? 'block' : 'none' }}
                      >
                        {isUsingStripe ? (
                          <StripeCreditCardForm />
                        ) : (
                          <PaymentMethodForm
                            disabled={!values.isNewPaymentMethod}
                            clientToken={braintreeClientToken || ''}
                            hostedFieldsInstance={hostedFieldsInstance}
                            setHostedFieldsInstance={setHostedFieldsInstance}
                          />
                        )}
                      </Box>

                      {activeStep === 3 && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Review Your Order
                          </Typography>

                          {/* Example Order Summary (replace with real cart if needed) */}
                          <Box mb={2}>
                            <Typography>
                              <strong>Product:</strong> Anarchy Plugin Suite
                            </Typography>
                            <Typography>
                              <strong>Platform:</strong> Windows
                            </Typography>
                            <Typography>
                              <strong>Host App:</strong> Adobe Premiere Pro
                            </Typography>
                            <Typography>
                              <strong>Quantity:</strong> 1
                            </Typography>
                            <Typography>
                              <strong>Total:</strong> $299.00
                            </Typography>
                          </Box>

                          {/* Display User Info */}
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>Name:</strong> {values.firstName}{' '}
                            {values.lastName}
                          </Typography>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>Email:</strong> {values.email}
                          </Typography>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>Billing Address:</strong>
                          </Typography>
                          <Typography variant="body2">
                            {values.streetAddress}, {values.locality},{' '}
                            {values.region} {values.postalCode},{' '}
                            {values.countryName}
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box mt={4}>
                <Stack direction="row" spacing={2} justifyContent="center">
                  {activeStep > 0 && (
                    <Button variant="outlined" onClick={handleBack}>
                      Back
                    </Button>
                  )}

                  {/* Not last step = normal next button */}
                  {activeStep < steps.length - 1 && (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={!isStepValid()}
                    >
                      Next
                    </Button>
                  )}

                  {/* Last step = final submit */}
                  {activeStep === steps.length - 1 && (
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Processing...' : 'Complete Purchase'}
                    </Button>
                  )}
                </Stack>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default CheckoutPage;
